/**
 * Section → PDF-region maps (FEAT-2026-0005, Phase 2 polish).
 *
 * When the user expands an Accordion in the form, the live preview
 * highlights the corresponding region of the rendered PDF so the connection
 * between "what I'm editing" and "what changes" is immediate and visual.
 *
 * Coordinates here are in PDF POINTS (top-left origin). The PreviewPane
 * multiplies by the current zoom/scale to convert them to pixel positions
 * on the rendered Letter (612 × 792 pt) page.
 *
 * NOTE: the per-test template editor was retired — all reports are now
 * code-based. Only the cross-report "Shared Header & Footer" template stays
 * admin-editable, so this file now only carries its header/footer region map.
 */

export interface PdfRegion {
  x: number; y: number; w: number; h: number;
  /** Optional alternate visual style. 'frame' = highlight the margin ring
   *  AROUND the content (used for page margins) instead of a filled rect. */
  style?: 'block' | 'frame';
}

const PW = 612;
const PH = 792;

/** Convenience — strips that make up the page-margin frame highlight. */
export function pageMarginStrips(cfg: any): PdfRegion[] {
  const top    = cfg?.page?.top    ?? 50;
  const bottom = cfg?.page?.bottom ?? 50;
  const left   = cfg?.page?.left   ?? 50;
  const right  = cfg?.page?.right  ?? 50;
  return [
    { x: 0,          y: 0,            w: PW,             h: top },
    { x: 0,          y: PH - bottom,  w: PW,             h: bottom },
    { x: 0,          y: top,          w: left,           h: PH - top - bottom },
    { x: PW - right, y: top,          w: right,          h: PH - top - bottom },
  ];
}

/**
 * Region map for the cross-template "Shared Header & Footer" template. Two
 * top-level sections — the header band (logo + title + project/sample info)
 * and the footer band (lab address + page number + signoffs). Both are
 * approximate rectangles around the affected strip of the page.
 */
export function headerFooterRegions(_cfg: any): Record<string, PdfRegion> {
  // Use FACTORY-default heights for the highlight box. Editing HF values
  // doesn't move WHERE the band is; it changes WHAT'S inside, so a
  // factory-shaped rectangle is the right "you're editing this strip"
  // visual. (Per-test page.top edits move the header start, but those
  // live in the per-test config, not in HF.)
  const HEADER_DEFAULT_TOP = 50;
  const HEADER_HEIGHT      = 172;       // matches headerEndY math
  const FOOTER_DEFAULT_RESERVE = 58;    // matches FACTORY_HEADER_FOOTER_CONFIG.footer.reserveFromBottom
  return {
    page: { x: 0, y: 0, w: PW, h: PH, style: 'frame' as const },
    header: {
      x: 0, y: 0,
      w: PW,
      h: HEADER_DEFAULT_TOP + HEADER_HEIGHT,
    },
    footer: {
      x: 0, y: PH - FOOTER_DEFAULT_RESERVE,
      w: PW,
      h: FOOTER_DEFAULT_RESERVE,
    },
    schemaVersion: { x: 0, y: 0, w: 0, h: 0 },
  };
}

/**
 * Pick the region map for a given test type. Only the Shared Header &
 * Footer template is admin-editable now; every other test returns null
 * (no highlight overlay).
 */
export function regionsForTest(
  testName: string | null | undefined,
  cfg: any,
  _pageIndex: number = 0,
): Record<string, PdfRegion> | null {
  if (testName === 'Shared Header & Footer') {
    return headerFooterRegions(cfg);
  }
  return null;
}
